/**
 * AsHovercardWrapper Component
 * 
 * A wrapper component that manages the display of a hovercard attached to its child elements.
 * The hovercard content is provided as a child as-hovercard element and is shown on hover by default.
 * Uses @floating-ui/dom for robust positioning with collision detection and boundary awareness.
 * Uses Shadow DOM to protect positioning logic from CSS interference while keeping content styleable.
 * 
 * Usage:
 * ```html
 * <as-hovercard-wrapper>
 *   <some-element></some-element>
 *   <as-hovercard><img src="preview.jpg" alt="Preview"/></as-hovercard>
 * </as-hovercard-wrapper>
 * ```
 * 
 * Attributes:
 * - show: When present, displays the hovercard manually
 * - placement: Position of the hovercard (top, bottom, left, right, top-left, top-right, bottom-left, bottom-right)
 * - show-on-hover: When present, shows hovercard on hover (default behavior)
 * - toggle-on-click: When present, toggles hovercard visibility on click
 * - keep-open: When present, prevents hiding on pointer leave (hover mode) or outside clicks (click mode)
 * - delay: Delay in milliseconds before showing hovercard on hover (default: 200)
 * - hide-delay: Delay in milliseconds before hiding hovercard (default: 100)
 * - offset: Distance in pixels from the trigger element (default: 8)
 * 
 * Events:
 * - as-hovercard-show: Dispatched when hovercard is shown
 * - as-hovercard-hide: Dispatched when hovercard is hidden
 * - as-hovercard-close (listened): Closes the hovercard when received from child hovercard
 */

import { computePosition, flip, shift, offset } from '@floating-ui/dom';
import styleText from './as-hovercard-wrapper.css?raw';
import { sendEv } from '../../data/events.ts';

export class AsHovercardWrapper extends HTMLElement {
  static readonly tag = 'as-hovercard-wrapper' as const;

  static get observedAttributes() {
    return ['show', 'placement', 'show-on-hover', 'toggle-on-click', 'keep-open', 'delay', 'hide-delay', 'offset'];
  }

  private _showTimeout: number | null = null;
  private _hideTimeout: number | null = null;
  private _isHovering = false;
  private _outsideClickHandler: ((event: Event) => void) | null = null;
  private _hovercard: HTMLElement | null = null;
  private _triggerSlot: HTMLSlotElement;
  private _hovercardSlot: HTMLSlotElement;
  // Callers (e.g. as-option) sometimes connect the wrapper to the DOM before
  // appending its trigger/hovercard children, so connectedCallback's assignSlots()
  // runs with nothing to assign. Watch for later child changes and re-assign.
  private _childObserver: MutationObserver;
  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsHovercardWrapper._sheet
    ];
    this._setupShadowDOM();
    this._triggerSlot = this.shadowRoot!.querySelector('slot[name="trigger"]') as HTMLSlotElement;
    this._hovercardSlot = this.shadowRoot!.querySelector('slot[name="hovercard"]') as HTMLSlotElement;
    this._childObserver = new MutationObserver(() => this._assignSlots());
  }

  /**
   * Setup Shadow DOM structure and styles
   */
  private _setupShadowDOM(): void {
    this.shadowRoot!.innerHTML = `
      <div class="wrapper">
        <slot name="trigger"></slot>
        <div class="hovercard-container">
          <slot name="hovercard"></slot>
        </div>
      </div>
    `;
  }

  /**
   * Get configuration values with defaults
   */
  private get _config() {
    return {
      delay: parseInt(this.getAttribute('delay') ?? '200'),
      hideDelay: parseInt(this.getAttribute('hide-delay') ?? '100'),
      offset: parseInt(this.getAttribute('offset') ?? '8'),
      placement: this.getAttribute('placement') || 'bottom',
      showOnHover: this.getAttribute('show-on-hover') !== 'false',
      toggleOnClick: this.hasAttribute('toggle-on-click'),
      keepOpen: this.hasAttribute('keep-open')
    };
  }

  /**
   * Get the hovercard element and cache it
   */
  private _getHovercard(): HTMLElement | null {
    if (!this._hovercard) {
      this._hovercard = this.querySelector('as-hovercard') as HTMLElement;
    }
    return this._hovercard;
  }

  /**
   * Get the hovercard container in shadow DOM
   */
  private _getHovercardContainer(): HTMLElement {
    return this.shadowRoot!.querySelector('.hovercard-container') as HTMLElement;
  }

  /**
   * Map placement terms to Floating UI placements
   */
  private _mapPlacement(placement: string): string {
    const placementMap: { [key: string]: string } = {
      'top': 'top', 'bottom': 'bottom', 'left': 'left', 'right': 'right',
      'top-left': 'top-end', 'top-right': 'top-start',
      'bottom-left': 'bottom-end', 'bottom-right': 'bottom-start',
      'right-top': 'right-start', 'right-bottom': 'right-end',
      'left-top': 'left-start', 'left-bottom': 'left-end'
    };
    return placementMap[placement] || 'bottom';
  }

  /**
   * Clear all active timeouts
   */
  private _clearTimeouts(): void {
    if (this._showTimeout) clearTimeout(this._showTimeout);
    if (this._hideTimeout) clearTimeout(this._hideTimeout);
    this._showTimeout = this._hideTimeout = null;
  }

  /**
   * Show the hovercard with positioning
   */
  private async _showHovercard(): Promise<void> {
    const hovercard = this._getHovercard();
    const container = this._getHovercardContainer();
    if (!hovercard || !container) return;

    this._clearTimeouts();
    
    // Assign slots before positioning
    this._assignSlots();
    await this._updatePosition();
    
    container.setAttribute('data-show', '');
    this.setAttribute('data-active', '');
    
    this._setupOutsideClickListener();
    sendEv(this, 'as-hovercard-show');
  }

  /**
   * Hide the hovercard
   */
  private _hideHovercard(): void {
    const container = this._getHovercardContainer();
    if (!container) return;

    this._clearTimeouts();
    container.removeAttribute('data-show');
    this.removeAttribute('data-active');
    
    this._removeOutsideClickListener();
    sendEv(this, 'as-hovercard-hide');
  }

  /**
   * Assign elements to appropriate slots
   */
  private _assignSlots(): void {
    const children = Array.from(this.children);
    
    children.forEach(child => {
      if (child.tagName.toLowerCase() === 'as-hovercard') {
        child.setAttribute('slot', 'hovercard');
      } else {
        child.setAttribute('slot', 'trigger');
      }
    });
  }

  /**
   * Update hovercard position using Floating UI
   */
  private async _updatePosition(): Promise<void> {
    const container = this._getHovercardContainer();
    if (!container) return;

    const { placement, offset: offsetValue } = this._config;

    try {
      const { x, y } = await computePosition(this, container, {
        placement: this._mapPlacement(placement) as any,
        strategy: 'fixed', // Use fixed positioning strategy
        middleware: [offset(offsetValue), flip(), shift({ padding: 5 })],
      });

      Object.assign(container.style, {
        left: `${x}px`,
        top: `${y}px`,
        transform: 'none'
      });
    } catch (error) {
      console.warn('Failed to position hovercard:', error);
    }
  }

  /**
   * Unified event handler for all interactions
   */
  private _handleInteraction = (event: Event): void => {
    const { showOnHover, toggleOnClick, keepOpen, delay, hideDelay } = this._config;
    
    switch (event.type) {
      case 'pointerenter':
        // Only handle hover if not in click mode
        if (showOnHover && !toggleOnClick) {
          this._isHovering = true;
          this._clearTimeouts();
          this._showTimeout = window.setTimeout(() => {
            if (this._isHovering) this._showHovercard();
          }, delay);
        }
        break;
        
      case 'pointerleave':
        // Only handle hover if not in click mode
        if (showOnHover && !toggleOnClick && !keepOpen) {
          this._isHovering = false;
          this._clearTimeouts();
          this._hideTimeout = window.setTimeout(() => {
            if (!this._isHovering) this._hideHovercard();
          }, hideDelay);
        }
        break;
        
      case 'click':
        if (toggleOnClick) {
          const container = this._getHovercardContainer();
          const isCurrentlyShowing = container?.hasAttribute('data-show');
          
          // Check if the click originated from inside the hovercard content
          const target = event.target as Node;
          const hovercard = this._getHovercard();
          const isClickFromHovercard = hovercard?.contains(target) ?? false;
          
          // Ignore clicks that originate from inside the hovercard
          if (isClickFromHovercard) {
            return;
          }
          
          if (isCurrentlyShowing) {
            this._hideHovercard();
          } else {
            this._showHovercard();
          }
        }
        break;
    }
  }

  /**
   * Handle outside clicks for click mode
   */
  private _handleOutsideClick = (event: Event): void => {
    if (this._config.keepOpen) return;
    
    const target = event.target as Node;
    const hovercard = this._getHovercard();
    
    // Check if click is inside the wrapper (trigger element) or hovercard content
    const isInsideWrapper = this.contains(target);
    const isInsideHovercard = hovercard?.contains(target) ?? false;
    
    // Only close if the click is outside both the wrapper and hovercard
    if (!isInsideWrapper && !isInsideHovercard) {
      this._hideHovercard();
    }
  }

  /**
   * Setup outside click listener
   */
  private _setupOutsideClickListener(): void {
    if (this._config.toggleOnClick && !this._outsideClickHandler) {
      this._outsideClickHandler = this._handleOutsideClick;
      // Use bubble phase (false) instead of capture phase to ensure proper event handling
      document.addEventListener('click', this._outsideClickHandler, false);
    }
  }

  /**
   * Remove outside click listener
   */
  private _removeOutsideClickListener(): void {
    if (this._outsideClickHandler) {
      document.removeEventListener('click', this._outsideClickHandler, false);
      this._outsideClickHandler = null;
    }
  }

  /**
   * Handle close events from hovercard
   */
  private _handleCloseEvent = (event: Event): void => {
    event.stopPropagation();
    this._hideHovercard();
  }

  /**
   * Handle hovercard hover events
   */
  private _handleHovercardHover = (event: Event): void => {
    // Only handle if not in click mode
    if (!this._config.toggleOnClick) {
      if (event.type === 'pointerenter') {
        this._isHovering = true;
        this._clearTimeouts();
      } else if (event.type === 'pointerleave' && !this._config.keepOpen) {
        this._isHovering = false;
        this._hideTimeout = window.setTimeout(() => {
          if (!this._isHovering) this._hideHovercard();
        }, this._config.hideDelay);
      }
    }
  }

  /**
   * Setup all event listeners
   */
  private _setupEvents(): void {
    // Listen on the host element for interactions
    this.addEventListener('pointerenter', this._handleInteraction);
    this.addEventListener('pointerleave', this._handleInteraction);
    this.addEventListener('click', this._handleInteraction);
    this.addEventListener('as-hovercard-close', this._handleCloseEvent);
    
    // Listen for hovercard hover events on the slotted elements
    this._hovercardSlot.addEventListener('slotchange', () => {
      const hovercard = this._getHovercard();
      if (hovercard) {
        hovercard.addEventListener('pointerenter', this._handleHovercardHover);
        hovercard.addEventListener('pointerleave', this._handleHovercardHover);
      }
    });
  }

  /**
   * Cleanup all event listeners and timeouts
   */
  private _cleanup(): void {
    this._clearTimeouts();
    this._removeOutsideClickListener();
    this.removeEventListener('pointerenter', this._handleInteraction);
    this.removeEventListener('pointerleave', this._handleInteraction);
    this.removeEventListener('click', this._handleInteraction);
    this.removeEventListener('as-hovercard-close', this._handleCloseEvent);
    
    // Clean up hovercard event listeners
    const hovercard = this._getHovercard();
    if (hovercard) {
      hovercard.removeEventListener('pointerenter', this._handleHovercardHover);
      hovercard.removeEventListener('pointerleave', this._handleHovercardHover);
    }
  }

  /**
   * Handle attribute changes without full re-render
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (!this.isConnected || oldValue === newValue) return;

    switch (name) {
      case 'show':
        if (newValue !== null) {
          this._showHovercard();
        } else {
          this._hideHovercard();
        }
        break;
        
      case 'placement':
      case 'offset':
        // Update position if currently visible
        const container = this._getHovercardContainer();
        if (container?.hasAttribute('data-show')) {
          this._updatePosition();
        }
        break;
    }
  }

  connectedCallback(): void {
    this._assignSlots();
    this._setupEvents();
    this._childObserver.observe(this, { childList: true });

    // Show immediately if show attribute is present
    if (this.hasAttribute('show')) {
      setTimeout(() => this._showHovercard(), 0);
    }
  }

  disconnectedCallback(): void {
    this._childObserver.disconnect();
    this._cleanup();
  }

  /**
   * Get child bounds for external positioning
   */
  get childBounds(): DOMRect {
    return this.getBoundingClientRect();
  }
}

customElements.define(AsHovercardWrapper.tag, AsHovercardWrapper);
